import React, { useState, useEffect } from 'react';
import { Shield, Save, RefreshCw, AlertTriangle, CheckCircle, Search, ChevronLeft } from 'lucide-react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useApp } from '../contexts/AppContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { permissionsAPI } from '../services/api';

const AVAILABLE_PAGES = [
  { path: '/notifications', label: 'sidebar.notifications' },
  { path: '/sessions', label: 'sidebar.sessions' },
  { path: '/organizations', label: 'sidebar.organizations' },
  { path: '/trainers', label: 'sidebar.trainers' },
  { path: '/students', label: 'sidebar.students' },
  { path: '/analytics', label: 'sidebar.analytics' },
  { path: '/exercises', label: 'sidebar.exercises' },
  { path: '/finance', label: 'sidebar.finance' },
  { path: '/permissions', label: 'sidebar.permissions' },
  { path: '/roles', label: 'sidebar.roles' },
  { path: '*', label: 'permissionsPage.allAccess' }
];

const PermissionsPage = () => {
  const { colors } = useTheme();
  const { userRole } = useApp();
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState('trainer');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  const [rolePermissions, setRolePermissions] = useState([]);
  const [rolePages, setRolePages] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [roles, setRoles] = useState([]); 
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [originalPages, setOriginalPages] = useState([]);

  useEffect(() => {
    if (userRole !== 'super_admin') {
      setError(t('permissionsPage.messages.accessDeniedDesc'));
      setLoading(false);
      return;
    }
    loadInitialData();
  }, [userRole]);

  useEffect(() => {
    if (selectedRole && roles.length > 0) {
      loadRolePermissions();
    }
  }, [selectedRole, roles]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [availableResponse, rolesResponse] = await Promise.all([
        permissionsAPI.getAvailablePermissions(),
        permissionsAPI.getAllRoles()
      ]);
      
      const perms = availableResponse.data?.data || availableResponse.data || {};
      setAvailablePermissions(perms);
      
      const dynamicRoles = rolesResponse.data?.data || rolesResponse.data || [];
      setRoles(dynamicRoles);
      
      if (dynamicRoles.length > 0 && !selectedRole) {
        setSelectedRole(dynamicRoles[0].role);
      }
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(t('permissionsPage.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getRolePermissions(selectedRole);
      const data = response.data?.data || response.data || {};
      const permissions = data.permissions || [];
      const pages = data.pages || [];
      
      setRolePermissions(permissions);
      setOriginalPermissions([...permissions]);
      setRolePages(pages);
      setOriginalPages([...pages]);
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading role data:', err);
      setError(t('permissionsPage.messages.loadRoleError', { role: selectedRole }));
    } finally {
      setLoading(false);
    }
  };

  const checkChanges = (newPerms, newPages) => {
    const permChanged = JSON.stringify([...originalPermissions].sort()) !== JSON.stringify([...newPerms].sort());
    const pageChanged = JSON.stringify([...originalPages].sort()) !== JSON.stringify([...newPages].sort());
    setHasChanges(permChanged || pageChanged);
  };

  const togglePermission = (permission) => {
    let updatedPermissions;
    if (rolePermissions.includes(permission)) {
      updatedPermissions = rolePermissions.filter(p => p !== permission);
    } else {
      updatedPermissions = [...rolePermissions, permission];
    }
    setRolePermissions(updatedPermissions);
    checkChanges(updatedPermissions, rolePages);
  };

  const togglePage = (pagePath) => {
    let updatedPages;
    if (rolePages.includes(pagePath)) {
      updatedPages = rolePages.filter(p => p !== pagePath);
    } else {
      updatedPages = [...rolePages, pagePath];
    }
    setRolePages(updatedPages);
    checkChanges(rolePermissions, updatedPages);
  };

  const toggleCategory = (category, categoryPermissions) => {
    if (!Array.isArray(categoryPermissions)) return;

    const permissionActions = categoryPermissions.map(p => p.actionType);
    const allSelected = permissionActions.every(p => rolePermissions.includes(p));

    let updatedPermissions;
    if (allSelected) {
      updatedPermissions = rolePermissions.filter(p => !permissionActions.includes(p));
    } else {
      const newPermissions = permissionActions.filter(p => !rolePermissions.includes(p));
      updatedPermissions = [...rolePermissions, ...newPermissions];
    }

    setRolePermissions(updatedPermissions);
    checkChanges(updatedPermissions, rolePages);
  };

  const savePermissions = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Save permissions
      await permissionsAPI.setRolePermissions(selectedRole, rolePermissions);
      
      // Save pages using the new endpoint
      if (permissionsAPI.setRolePages) {
        await permissionsAPI.setRolePages(selectedRole, rolePages);
      }
      
      setOriginalPermissions([...rolePermissions]);
      setOriginalPages([...rolePages]);
      setHasChanges(false);
      setSuccess(t('permissionsPage.messages.saveSuccess', { role: selectedRole }));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving role data:', err);
      setError(t('permissionsPage.messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const resetPermissions = () => {
    setRolePermissions([...originalPermissions]);
    setHasChanges(false);
  };

  const getFilteredPermissions = () => {
    if (!searchTerm) return availablePermissions;

    const filtered = {};
    Object.entries(availablePermissions).forEach(([category, categoryPermissions]) => {
      if (!Array.isArray(categoryPermissions)) return;

      const categoryLabel = t(`permissionsPage.categories.${category}`) || category;

      const matchedPermissions = categoryPermissions.filter(p =>
        p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.actionType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        categoryLabel.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (matchedPermissions.length > 0) {
        filtered[category] = matchedPermissions;
      }
    });
    return filtered;
  };

  const SuccessNotification = ({ message, onClose }) => {
    if (!message) return null;

    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-green-900/50 to-green-800/50 border border-green-600 rounded-2xl shadow-sm backdrop-blur-sm">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <p className="text-green-300 font-medium">{message}</p>
          </div>
          <button onClick={onClose} className="ml-3 text-green-400 hover:text-green-200 hover:scale-110 transition-transform">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  if (userRole !== 'super_admin') {
    return (
      <div className={`flex h-full ${colors.bgPrimary} p-6`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}>
              <AlertTriangle className={`w-8 h-8 ${colors.emptyStateIcon}`} />
            </div>
            <h2 className={`text-xl font-semibold ${colors.textPrimary} mb-2`}>{t('permissionsPage.messages.accessDenied')}</h2>
            <p className={colors.textTertiary}>{t('permissionsPage.messages.accessDeniedDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredPermissions = getFilteredPermissions();

  return (
    <div className={`flex flex-col lg:flex-row h-auto min-h-full lg:h-full ${colors.bgPrimary} p-3 sm:p-6 lg:space-x-6 gap-6 overflow-y-auto lg:overflow-hidden custom-scrollbar`}>
      {/* Filters Sidebar */}
      <div className={`${isFiltersOpen ? 'w-full lg:w-80' : 'w-full lg:w-16'} ${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg p-5 lg:p-6 flex flex-col transition-all duration-300 ease-in-out shrink-0`}>
        <div className="flex items-center justify-between mb-6">
          {/* On mobile the panel is always full width, so the header always shows */}
          <h2 className={`${isFiltersOpen ? '' : 'lg:hidden'} text-lg font-bold ${colors.textPrimary} flex items-center`}>
            <Shield className={`w-5 h-5 mr-2 ${colors.textSecondary}`} />
            {t('permissionsPage.roleSettings')}
          </h2>
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className={`hidden lg:block p-2 ${colors.bgHoverStrong} rounded-lg transition-colors ml-auto`}
          >
            {isFiltersOpen ? (
              <ChevronLeft className={`w-5 h-5 ${colors.textSecondary}`} />
            ) : (
              <Shield className={`w-5 h-5 ${colors.textSecondary}`} />
            )}
          </button>
        </div>

        {isFiltersOpen && (
          <div className="space-y-6">
            {/* Role Selector */}
            <div>
              <h3 className={`text-sm font-semibold ${colors.textSecondary} mb-3`}>{t('permissionsPage.selectRole')}</h3>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className={`w-full px-4 py-2.5 ${colors.inputBg} ${colors.inputBorder} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} text-sm shadow-inner ${colors.inputText}`}
              >
                {roles.map(roleItem => {
                   const r = roleItem.role || roleItem.id;
                   const isCore = ['super_admin', 'organization_manager', 'trainer', 'student', 'manager'].includes(r);
                   const displayName = isCore ? t(`permissionsPage.roles.${r}.name`) : r;
                   return (
                     <option key={r} value={r}>{displayName}</option>
                   );
                })}
              </select>
            </div>

            {/* Role Description */}
            {selectedRole && (
              <div className={`p-4 rounded-xl bg-gradient-to-r ${selectedRole === 'super_admin' ? 'from-red-500 to-red-600' :
                  ['manager', 'organization_manager'].includes(selectedRole) ? 'from-purple-500 to-purple-600' :
                    selectedRole === 'trainer' ? 'from-blue-500 to-blue-600' :
                      selectedRole === 'student' ? 'from-green-500 to-green-600' :
                      'from-slate-500 to-slate-600'
                } text-white shadow-lg`}>
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold mb-1">
                        {['super_admin', 'organization_manager', 'trainer', 'student', 'manager'].includes(selectedRole) 
                            ? t(`permissionsPage.roles.${selectedRole}.name`) 
                            : selectedRole}
                    </div>
                    <div className="text-sm opacity-90">
                        {['super_admin', 'organization_manager', 'trainer', 'student', 'manager'].includes(selectedRole)
                            ? t(`permissionsPage.roles.${selectedRole}.description`)
                            : t('rolesPage.manageGroups')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Search */}
            <div>
              <h3 className={`text-sm font-semibold ${colors.textSecondary} mb-3`}>{t('permissionsPage.searchPermissions')}</h3>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.textTertiary}`} />
                <input
                  type="text"
                  placeholder={t('permissionsPage.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 ${colors.inputBg} ${colors.inputBorder} rounded-xl focus:outline-none focus:ring-2 ${colors.inputFocus} text-sm shadow-inner ${colors.inputText} ${colors.inputPlaceholder}`}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`space-y-2 pt-4 border-t ${colors.border}`}>
              {hasChanges && (
                <button
                  onClick={resetPermissions}
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-2.5 ${colors.btnSecondaryBg} ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} rounded-xl ${colors.btnSecondaryHover} transition-all duration-200 font-semibold shadow-sm`}
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>{t('permissionsPage.reset')}</span>
                </button>
              )}
              <button
                onClick={savePermissions}
                disabled={!hasChanges || saving}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>{t('permissionsPage.saving')}</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{t('permissionsPage.saveChanges')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg overflow-hidden flex flex-col min-h-[320px] lg:min-h-[500px] lg:h-full`}>
        {/* Header */}
        <div className={`p-6 border-b ${colors.tableBorder} ${colors.tableHeader} flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className={`text-2xl font-bold ${colors.textPrimary}`}>{t('permissionsPage.pageTitle')}</h1>
              <div className="px-3 py-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full">
                <span className="text-xs text-white font-bold">{t('permissionsPage.permissionCount', { count: rolePermissions.length })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="p-6 flex-shrink-0">
          <SuccessNotification message={success} onClose={() => setSuccess(null)} />

          {error && (
            <div className="mb-6 p-4 bg-gradient-to-r from-red-900/50 to-red-800/50 border border-red-600 rounded-2xl shadow-sm backdrop-blur-sm">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <div className="ml-3 flex-1">
                  <p className="text-red-300 font-medium">{error}</p>
                </div>
                <button onClick={() => setError(null)} className="ml-3 text-red-400 hover:text-red-200">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Permissions Content */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="large" />
              <span className={`ml-2 ${colors.textTertiary}`}>{t('organizationDetailsPage.loading')}</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Page Visibility Section */}
              <div className={`${colors.cardBg} ${colors.cardBorder} rounded-2xl p-6 shadow-sm`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-base font-semibold ${colors.textPrimary} flex items-center`}>
                    <Shield className={`w-4 h-4 mr-2 ${rolePages.length > 0 ? 'text-green-500' : 'text-gray-500'}`} />
                    {t('permissionsPage.pageVisibility')}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AVAILABLE_PAGES.map(page => {
                    const isChecked = rolePages.includes(page.path);
                    return (
                      <div
                        key={page.path}
                        className={`flex items-start space-x-3 p-3 border rounded-xl transition-all duration-200 cursor-pointer ${isChecked
                          ? 'bg-gradient-to-r from-green-500/10 to-green-600/10 border-green-500/50'
                          : `${colors.tableRowHover} ${colors.cardBorder}`
                        }`}
                        onClick={() => togglePage(page.path)}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePage(page.path)}
                          className="mt-0.5 w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500 flex-shrink-0 cursor-pointer"
                        />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium ${isChecked ? 'text-green-400' : colors.textPrimary}`}>
                            {t(page.label)}
                          </div>
                          <div className="mt-1">
                            <code className={`text-xs px-2 py-0.5 rounded ${colors.inputBg} ${colors.textTertiary}`}>
                              {page.path}
                            </code>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Permissions Categories */}
              {Object.keys(filteredPermissions).length > 0 ? Object.entries(filteredPermissions).map(([category, categoryPermissions]) => {
                if (!Array.isArray(categoryPermissions)) return null;

                const allSelected = categoryPermissions.every(p => rolePermissions.includes(p.actionType));
                const someSelected = categoryPermissions.some(p => rolePermissions.includes(p.actionType));

                return (
                  <div key={category} className={`${colors.cardBg} ${colors.cardBorder} rounded-2xl p-6 shadow-sm`}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className={`text-base font-semibold ${colors.textPrimary} capitalize flex items-center`}>
                        <Shield className={`w-4 h-4 mr-2 ${allSelected ? 'text-green-500' : someSelected ? 'text-yellow-500' : 'text-gray-500'}`} />
                        {t(`permissionsPage.categories.${category}`) || category}
                      </h3>
                      <button
                        onClick={() => toggleCategory(category, categoryPermissions)}
                        className={`text-sm px-4 py-1.5 rounded-xl transition-all duration-200 font-semibold ${allSelected
                            ? 'bg-gradient-to-r from-green-600 to-green-700 text-white'
                            : someSelected
                              ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-white'
                              : `${colors.btnSecondaryBg} ${colors.btnSecondaryText}`
                          }`}
                      >
                        {allSelected ? t('permissionsPage.deselectAll') : t('permissionsPage.selectAll')}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {categoryPermissions.map(permission => {
                        const isChecked = rolePermissions.includes(permission.actionType);

                        return (
                          <div
                            key={permission.actionType}
                            className={`flex items-start space-x-3 p-3 border rounded-xl transition-all duration-200 cursor-pointer ${isChecked
                                ? 'bg-gradient-to-r from-blue-500/10 to-blue-600/10 border-blue-500/50'
                                : `${colors.tableRowHover} ${colors.cardBorder}`
                              }`}
                            onClick={() => togglePermission(permission.actionType)}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => togglePermission(permission.actionType)}
                              className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                            />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium ${isChecked ? 'text-blue-400' : colors.textPrimary}`}>
                                {permission.description}
                              </div>
                              <div className="mt-1">
                                <code className={`text-xs px-2 py-0.5 rounded ${colors.inputBg} ${colors.textTertiary}`}>
                                  {permission.actionType}
                                </code>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }) : Object.keys(filteredPermissions).length > 0 && (
                <div className="text-center py-12">
                  <div className={`w-16 h-16 ${colors.emptyStateBg} rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg`}>
                    <Shield className={`w-8 h-8 ${colors.emptyStateIcon}`} />
                  </div>
                  <p className={colors.textTertiary}>
                    {searchTerm
                      ? t('permissionsPage.messages.noMatchFound', { query: searchTerm })
                      : t('permissionsPage.messages.noPermissionsFound')
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;